import { useState, useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMissions, useCreateMission, useUpdateMission, useDeleteMission, useMissionSegments, useCreateSegment, useUpdateSegment, useDeleteSegment } from "@/hooks/use-missions";
import { useClients } from "@/hooks/use-clients";
import { useProspects } from "@/hooks/use-prospects";
import { useResellers } from "@/hooks/use-resellers";
import { useCommercials, useTechnicians, useProfiles } from "@/hooks/use-profiles";
import { useRobots, useVehicles } from "@/hooks/use-inventory";
import { useCreateTask, useUpdateTask } from "@/hooks/use-tasks";
import { useLocation } from "wouter";
import { authFetch } from "@/lib/authFetch";
import { queryClient } from "@/lib/queryClient";
import { extractLocation } from "@/lib/utils";
import { handlePlainTextCopy } from "@/lib/plainTextCopy";
import { resolveMissionTitle } from "@/lib/mission-display";
import { getRoleLevel, ROLE_LEVEL } from "@shared/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/status-badge";
import { SearchSelect } from "@/components/search-select";
import { MultiBadgeSelect } from "@/components/multi-badge-select";
import type { MultiBadgeOption } from "@/components/multi-badge-select";
import AddressAutocomplete from "@/components/address-autocomplete";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Plus, ClipboardList, Pencil, MoreHorizontal, Eye, Circle, Trash2, Car, Hotel, Wrench, GraduationCap, MapPin, Calendar as CalendarIcon, List, CalendarDays, Sun, ChevronDown, ChevronUp, Link2, Video, Navigation, Clock, Paperclip, Download, FileText, Upload, X, CheckCircle2 } from "lucide-react";
import { MISSION_TYPES } from "@shared/missionTypeConfig";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAuth } from "@/hooks/use-auth";
import { useAvailableReplacementRobots } from "@/hooks/use-client-robot-assignments";
import type { ClientRobotAssignment } from "@/hooks/use-client-robot-assignments";
import { useToast } from "@/hooks/use-toast";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { fr } from "date-fns/locale";
import { MissionWeekView, MissionDayView } from "@/pages/missions/week-view";
import { WeeklyPdfActions } from "@/components/weekly-pdf-actions";
import type { CreateMissionRequest, UpdateMissionRequest, Client, Mission, TechnicianAssignment, Profile, Vehicle, Prospect, MissionSegment } from "@shared/schema";
import { profileIdSchema } from "@shared/identity";
import type { MissionAvailabilityData, MissionBatchAvailability } from "@shared/missionAvailability";
import { isRobotFleetAvailable } from "@shared/robotStatusConfig";

const MISSION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(MISSION_TYPES).map(([k, v]) => [k, v.label])
);

const DOC_CATEGORIE_LABELS: Record<string, string> = {
  hotel: "Réservation hôtel",
  laissez_passer: "Laissez-passer",
  plan: "Plan / accès",
  autre: "Autre",
};

const MISSION_STATUS_LABELS: Record<string, string> = {
  option: "Option",
  confirme: "Confirmé",
  en_cours: "En cours",
  termine: "Terminé",
  annule: "Annulé",
};

const TASK_TYPE_LABELS: Record<string, string> = {
  reservation_hotel: "Réservation hôtel",
  reservation_train: "Réservation train",
  location_vehicule: "Location véhicule",
  preparation_materiel: "Préparation matériel",
};

const SEGMENT_TYPE_LABELS: Record<string, string> = {
  intervention: "Intervention",
  trajet_aller: "Trajet aller",
  trajet_retour: "Trajet retour",
  nuit_hotel: "Nuit hôtel",
  formation: "Formation",
  deplacement_partiel: "Déplacement partiel",
};

const SEGMENT_TYPE_CONFIG: Record<string, { color: string; icon: typeof Wrench }> = {
  intervention: { color: "text-blue-600 bg-blue-100 border-blue-200", icon: Wrench },
  trajet_aller: { color: "text-orange-600 bg-orange-100 border-orange-200", icon: Car },
  trajet_retour: { color: "text-orange-600 bg-orange-100 border-orange-200", icon: Car },
  nuit_hotel: { color: "text-purple-600 bg-purple-100 border-purple-200", icon: Hotel },
  formation: { color: "text-indigo-600 bg-indigo-100 border-indigo-200", icon: GraduationCap },
  deplacement_partiel: { color: "text-gray-600 bg-gray-100 border-gray-200", icon: MapPin },
};

const ENRICHED_TYPES = ["installation", "demonstration"];

function AvailabilityBadge({ resourceId, type, availability, tooltipSide }: {
  resourceId: string;
  type: "technicians" | "robots" | "vehicles" | "commercials";
  availability: MissionAvailabilityData | null;
  tooltipSide?: "top" | "right" | "bottom" | "left";
}) {
  if (!availability) return null;
  const rawConflicts = availability[type]?.[resourceId];
  const conflicts = rawConflicts?.map(name => MISSION_TYPE_LABELS[name] || name) || [];
  if (conflicts.length === 0) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500 flex-shrink-0" data-testid={`badge-available-${resourceId}`} />
          </TooltipTrigger>
          <TooltipContent side={tooltipSide || "right"}>
            <p className="text-xs">Disponible</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  const color = conflicts.length >= 2 ? "text-red-500 fill-red-500" : "text-orange-500 fill-orange-500";
  const label = conflicts.length >= 2 ? "Occupé" : "Partiellement occupé";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Circle className={`w-3 h-3 flex-shrink-0 ${color}`} data-testid={`badge-busy-${resourceId}`} />
        </TooltipTrigger>
        <TooltipContent side={tooltipSide || "right"}>
          <div className="text-xs">
            <p className="font-semibold">{label}</p>
            {conflicts.map((name, i) => (
              <p key={i} className="text-muted-foreground">• {name}</p>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function MissionConflictSummary({ mission, verdict }: {
  mission: any;
  verdict?: MissionBatchAvailability;
}) {
  if (!verdict) return <Circle className="w-3.5 h-3.5 fill-muted text-muted-foreground" />;
  if (!verdict.hasResources) return <span className="text-xs text-muted-foreground">—</span>;

  return verdict.hasConflict
    ? <Circle className="w-3.5 h-3.5 fill-red-500 text-red-500" />
    : <Circle className="w-3.5 h-3.5 fill-emerald-500 text-emerald-500" />;
}

function SegmentTimeline({ segments, allProfiles, allVehicles }: {
  segments: MissionSegment[];
  allProfiles: Profile[];
  allVehicles: Vehicle[];
}) {
  if (!segments || segments.length === 0) return <p className="text-xs text-muted-foreground">Aucun segment planifié</p>;

  const grouped = segments.reduce((acc, seg) => {
    if (!acc[seg.date]) acc[seg.date] = [];
    acc[seg.date].push(seg);
    return acc;
  }, {} as Record<string, MissionSegment[]>);

  const sortedDates = Object.keys(grouped).sort();

  return (
    <div className="space-y-3">
      {sortedDates.map(dateStr => {
        const dateObj = new Date(dateStr + "T00:00:00");
        const daySegs = grouped[dateStr].sort((a, b) => a.ordre - b.ordre);
        return (
          <div key={dateStr} className="border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-3 py-1.5 flex items-center gap-2 border-b">
              <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{format(dateObj, 'EEEE d MMMM', { locale: fr })}</span>
            </div>
            <div className="divide-y">
              {daySegs.map(seg => {
                const config = SEGMENT_TYPE_CONFIG[seg.type] || SEGMENT_TYPE_CONFIG.intervention;
                const Icon = config.icon;
                const techNames = (seg.techniciensIds || []).map(id => {
                  const p = allProfiles.find(pr => pr.id === id);
                  return p ? `${p.firstName} ${p.lastName}` : "";
                }).filter(Boolean);
                const vehicle = seg.vehiculeId ? allVehicles.find(v => v.id === seg.vehiculeId) : null;

                return (
                  <div key={seg.id} className="px-3 py-2 flex items-center gap-3" data-testid={`segment-${seg.id}`}>
                    <div className={`flex items-center justify-center w-7 h-7 rounded-full border ${config.color}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${config.color}`}>
                          {SEGMENT_TYPE_LABELS[seg.type] || seg.type}
                        </span>
                        {seg.heureDebut && seg.heureFin && (
                          <span className="text-xs text-muted-foreground">{seg.heureDebut} → {seg.heureFin}</span>
                        )}
                        {seg.label && <span className="text-xs text-muted-foreground">— {seg.label}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {techNames.length > 0 && (
                          <span className="text-xs text-muted-foreground">{techNames.join(", ")}</span>
                        )}
                        {vehicle && (
                          <span className="text-xs text-muted-foreground">🚗 {vehicle.brandModel || vehicle.licensePlate}</span>
                        )}
                      </div>
                      {seg.notes && <p className="text-xs text-muted-foreground mt-0.5">{seg.notes}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SegmentEditor({ missionId, allProfiles, allVehicles, techAssignments }: {
  missionId: string;
  allProfiles: Profile[];
  allVehicles: Vehicle[];
  techAssignments: TechnicianAssignment[];
}) {
  const { data: segments = [], isLoading } = useMissionSegments(missionId);
  const createSeg = useCreateSegment();
  const updateSeg = useUpdateSegment();
  const deleteSeg = useDeleteSegment();
  const { toast } = useToast();

  const [addingSegment, setAddingSegment] = useState(false);
  const [editingSegId, setEditingSegId] = useState<string | null>(null);
  const [segForm, setSegForm] = useState({
    type: "intervention" as string,
    date: "",
    heureDebut: "",
    heureFin: "",
    label: "",
    techniciensIds: [] as string[],
    vehiculeId: "",
    notes: "",
  });

  const missionTechIds = techAssignments.map(t => t.technicianId);
  const availableTechs = allProfiles.filter(
    p => missionTechIds.includes(p.id) && (p.isActive || segForm.techniciensIds.includes(p.id)),
  );

  const resetSegForm = () => {
    setSegForm({ type: "intervention", date: "", heureDebut: "", heureFin: "", label: "", techniciensIds: [], vehiculeId: "", notes: "" });
    setAddingSegment(false);
    setEditingSegId(null);
  };

  const openEdit = (seg: MissionSegment) => {
    setSegForm({
      type: seg.type,
      date: seg.date,
      heureDebut: seg.heureDebut || "",
      heureFin: seg.heureFin || "",
      label: seg.label || "",
      techniciensIds: (seg.techniciensIds || []) as string[],
      vehiculeId: seg.vehiculeId || "",
      notes: seg.notes || "",
    });
    setEditingSegId(seg.id);
    setAddingSegment(true);
  };

  const handleSave = async () => {
    if (!segForm.date) { toast({ variant: "destructive", title: "Date requise" }); return; }
    try {
      const payload = {
        ...segForm,
        vehiculeId: segForm.vehiculeId || null,
        techniciensIds: segForm.techniciensIds.length > 0 ? segForm.techniciensIds : [],
        heureDebut: segForm.heureDebut || null,
        heureFin: segForm.heureFin || null,
        label: segForm.label || null,
        notes: segForm.notes || null,
        ordre: editingSegId ? (segments.find(s => s.id === editingSegId)?.ordre || 0) : segments.length,
      };
      if (editingSegId) {
        await updateSeg.mutateAsync({ id: editingSegId, missionId, ...payload });
        toast({ title: "Segment mis à jour" });
      } else {
        await createSeg.mutateAsync({ missionId, ...payload });
        toast({ title: "Segment ajouté" });
      }
      resetSegForm();
    } catch {
      toast({ variant: "destructive", title: "Erreur lors de la sauvegarde" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteSeg.mutateAsync({ id, missionId });
      toast({ title: "Segment supprimé" });
    } catch {
      toast({ variant: "destructive", title: "Erreur" });
    }
  };

  if (isLoading) return <p className="text-xs text-muted-foreground">Chargement...</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Planning détaillé</span>
        {!addingSegment && (
          <Button variant="outline" size="sm" type="button" onClick={() => setAddingSegment(true)} data-testid="btn-add-segment">
            <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter un segment
          </Button>
        )}
      </div>

      {segments.length > 0 && (
        <div className="space-y-1">
          {segments.map(seg => {
            const config = SEGMENT_TYPE_CONFIG[seg.type] || SEGMENT_TYPE_CONFIG.intervention;
            const Icon = config.icon;
            const techNames = (seg.techniciensIds || []).map(id => {
              const p = allProfiles.find(pr => pr.id === id);
              return p ? `${p.firstName}` : "";
            }).filter(Boolean);

            return (
              <div key={seg.id} className="flex items-center gap-2 px-2 py-1.5 rounded border bg-card group" data-testid={`seg-row-${seg.id}`}>
                <div className={`flex items-center justify-center w-6 h-6 rounded-full border ${config.color}`}>
                  <Icon className="h-3 w-3" />
                </div>
                <span className="text-xs font-medium w-20 truncate">{SEGMENT_TYPE_LABELS[seg.type]}</span>
                <span className="text-xs text-muted-foreground w-24">{seg.date}</span>
                {seg.heureDebut && seg.heureFin && (
                  <span className="text-xs text-muted-foreground w-24">{seg.heureDebut}→{seg.heureFin}</span>
                )}
                {techNames.length > 0 && <span className="text-xs text-muted-foreground truncate flex-1">{techNames.join(", ")}</span>}
                {seg.label && <span className="text-xs text-muted-foreground truncate">{seg.label}</span>}
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="sm" type="button" className="h-6 w-6 p-0" onClick={() => openEdit(seg)} data-testid={`btn-edit-seg-${seg.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" type="button" className="h-6 w-6 p-0 text-destructive" onClick={() => handleDelete(seg.id)} data-testid={`btn-delete-seg-${seg.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {addingSegment && (
        <div className="border rounded-lg p-3 space-y-3 bg-muted/30">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Type</Label>
              <Select value={segForm.type} onValueChange={v => setSegForm({...segForm, type: v})}>
                <SelectTrigger className="h-8 text-xs" data-testid="seg-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(SEGMENT_TYPE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Date</Label>
              <Input type="date" className="h-8 text-xs" value={segForm.date} onChange={e => setSegForm({...segForm, date: e.target.value})} data-testid="seg-date" />
            </div>
          </div>

          {segForm.type !== "nuit_hotel" && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Heure début</Label>
                <Input type="time" className="h-8 text-xs" value={segForm.heureDebut} onChange={e => setSegForm({...segForm, heureDebut: e.target.value})} data-testid="seg-heure-debut" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Heure fin</Label>
                <Input type="time" className="h-8 text-xs" value={segForm.heureFin} onChange={e => setSegForm({...segForm, heureFin: e.target.value})} data-testid="seg-heure-fin" />
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Label</Label>
            <Input className="h-8 text-xs" placeholder="ex: Jour 1, Trajet Lyon..." value={segForm.label} onChange={e => setSegForm({...segForm, label: e.target.value})} data-testid="seg-label" />
          </div>

          {availableTechs.length > 0 && (
            <div className="space-y-1">
              <Label className="text-xs">Techniciens</Label>
              <div className="flex flex-wrap gap-1.5">
                {availableTechs.map(t => {
                  const selected = segForm.techniciensIds.includes(t.id);
                  return (
                    <button
                      key={t.id}
                      type="button"
                      className={`px-2 py-0.5 rounded-full text-xs border transition-colors ${selected ? "bg-primary text-primary-foreground border-primary" : "bg-secondary text-secondary-foreground border-border hover:bg-secondary/80"}`}
                      onClick={() => {
                        setSegForm(prev => ({
                          ...prev,
                          techniciensIds: selected
                            ? prev.techniciensIds.filter(id => id !== t.id)
                            : [...prev.techniciensIds, t.id],
                        }));
                      }}
                      data-testid={`seg-tech-${t.id}`}
                    >
                      {t.firstName} {t.lastName}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Véhicule</Label>
            <Select value={segForm.vehiculeId || "none"} onValueChange={v => setSegForm({...segForm, vehiculeId: v === "none" ? "" : v})}>
              <SelectTrigger className="h-8 text-xs" data-testid="seg-vehicule">
                <SelectValue placeholder="Aucun" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {allVehicles?.map((v: Vehicle) => (
                  <SelectItem key={v.id} value={v.id}>{v.brandModel || v.licensePlate}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Input className="h-8 text-xs" value={segForm.notes} onChange={e => setSegForm({...segForm, notes: e.target.value})} data-testid="seg-notes" />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" type="button" onClick={resetSegForm}>Annuler</Button>
            <Button size="sm" type="button" onClick={handleSave} disabled={createSeg.isPending || updateSeg.isPending} data-testid="btn-save-segment">
              {editingSegId ? "Enregistrer" : "Ajouter"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MissionList() {
  const { profile } = useAuth();
  const { data: missions, isLoading } = useMissions();
  const { data: clients } = useClients();
  const { data: prospectsList } = useProspects();
  const { data: resellers } = useResellers();
  const { data: commercials } = useCommercials();
  const { data: allProfiles } = useProfiles();
  const { data: allRobots } = useRobots();
  const { data: allVehicles } = useVehicles();

  const [formData, setFormData] = useState<Partial<CreateMissionRequest> & { tiersType?: string; tiersId?: string }>({
    missionType: "demonstration",
    status: "option",
    clientId: "",
    tiersType: "prospect",
    tiersId: undefined,
    commercialId: profile?.id,
    createdBy: profile?.id,
    startDate: new Date(),
    endDate: new Date(),
    heureDebut: "",
    heureFin: "",
    arrivalTime: "09:00",
    demoStartTime: "10:00",
    eventName: "",
    description: "",
    journeeEntiere: false,
    lieuLibre: "",
    lienVisio: "",
    dateFin: undefined,
    adresseDepart: "",
    departLat: undefined,
    departLng: undefined,
    adresseArrivee: "",
    ariveeLat: undefined,
    ariveeLng: undefined,
  });

  // Robots du client sélectionné (tiersType=client)
  const { data: clientRobots } = useQuery<any[]>({
    queryKey: ['/api/clients', formData.tiersId, 'robots'],
    queryFn: () => authFetch(`/api/clients/${formData.tiersId}/robots`).then(r => r.json()),
    enabled: formData.tiersType === 'client' && !!formData.tiersId,
    staleTime: 60_000,
  });
  const { data: availableReplacementRobots = [] } = useAvailableReplacementRobots();
  const createMutation = useCreateMission();
  const updateMutation = useUpdateMission();
  const deleteMutation = useDeleteMission();
  const createTaskMutation = useCreateTask();
  const updateTaskMutation = useUpdateTask();
  const { toast } = useToast();
  const fromTaskProcessed = useRef(false);
  const userLevel = getRoleLevel(profile?.accessRole);
  const hasGestionPlanning = !!(profile as any)?.gestionPlanning;
  const canEdit = userLevel >= ROLE_LEVEL.manager || hasGestionPlanning;
  const canDeleteMission = (m: any) =>
    userLevel >= ROLE_LEVEL.manager || hasGestionPlanning || (!!profile?.id && m.createdBy === profile.id);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<"week5" | "week7" | "day" | "list">("week5");
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() =>
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const [currentDay, setCurrentDay] = useState<Date>(() => new Date());
  const [listPeriod, setListPeriod] = useState<"all" | "this_week" | "this_month" | "custom">("all");
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMission, setEditingMission] = useState<Mission | null>(null);
  const [detailMission, setDetailMission] = useState<any | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingMissionId, setLoadingMissionId] = useState<string | null>(null);

  const [selectedCommercialIds, setSelectedCommercialIds] = useState<string[]>([]);
  const [techAssignments, setTechAssignments] = useState<TechnicianAssignment[]>([]);
  const [selectedRobotIds, setSelectedRobotIds] = useState<string[]>([]);
  const [clientRobotAssignments, setClientRobotAssignments] = useState<ClientRobotAssignment[]>([]);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([]);

  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskMission, setTaskMission] = useState<Mission | null>(null);
  const [taskType, setTaskType] = useState("reservation_hotel");
  const [taskTitle, setTaskTitle] = useState("");

  const [availability, setAvailability] = useState<MissionAvailabilityData | null>(null);
  const [missionAvailability, setMissionAvailability] = useState<Record<string, MissionBatchAvailability>>({});

  const [volet2Open, setVolet2Open] = useState(false);

  // ── Mission Documents ──────────────────────────────────────────────────────
  // Historique transitions (moteur d'états)
  const { data: entityEventsData = [] } = useQuery<any[]>({
    queryKey: ["/api/entity-events", "mission", detailMission?.id],
    queryFn: async () => {
      if (!detailMission?.id) return [];
      const r = await authFetch(`/api/entity-events?entityType=mission&entityId=${detailMission.id}`);
      if (!r.ok) return [];
      return r.json();
    },
    enabled: !!detailMission?.id && !!detailOpen,
    staleTime: 30_000,
  });

  const [missionDocs, setMissionDocs] = useState<any[]>([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadDocCategorie, setUploadDocCategorie] = useState("autre");
  const [uploadDocDesc, setUploadDocDesc] = useState("");
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [confirmDeleteDocId, setConfirmDeleteDocId] = useState<string | null>(null);
  const docInputRef = useRef<HTMLInputElement>(null);

  const [fromTaskId, setFromTaskId] = useState<string | null>(null);

  // Prefill depuis le planning (clic cellule vide)
  const planningPrefillProcessed = useRef(false);
  useEffect(() => {
    if (planningPrefillProcessed.current) return;
    const params = new URLSearchParams(window.location.search);
    const openCreate = params.get("openCreate");
    const planningDate = params.get("planningDate");
    const planningRobotId = params.get("planningRobotId");
    const planningVehicleId = params.get("planningVehicleId");
    const technicianId = params.get("technicianId");
    if (!openCreate) return;
    planningPrefillProcessed.current = true;

    if (planningDate) {
      const d = new Date(planningDate + "T12:00:00");
      setFormData(prev => ({ ...prev, startDate: d, endDate: d }));
    }
    if (technicianId) {
      setTechAssignments([{ technicianId, isRemote: false }]);
    }
    if (planningRobotId) {
      setSelectedRobotIds([planningRobotId]);
    }
    if (planningVehicleId) {
      setSelectedVehicleIds([planningVehicleId]);
    }

    setDialogOpen(true);
    window.history.replaceState({}, "", "/missions");
  }, []);

  // Ouvrir le détail mission depuis le planning (clic étiquette)
  const planningDetailProcessed = useRef(false);
  useEffect(() => {
    if (planningDetailProcessed.current) return;
    const params = new URLSearchParams(window.location.search);
    const openDetailId = params.get("openDetail");
    if (!openDetailId) return;
    planningDetailProcessed.current = true;

    // Fetch mission detail and open panel
    const fetchAndOpen = async () => {
      try {
        const res = await authFetch(`/api/missions/${openDetailId}`);
        if (res.ok) {
          const detail = await res.json();
          setDetailMission(detail);
          setDetailOpen(true);
        }
      } catch {}
    };
    fetchAndOpen();
    window.history.replaceState({}, "", "/missions");
  }, []);

  useEffect(() => {
    if (fromTaskProcessed.current) return;
    const params = new URLSearchParams(window.location.search);
    const taskId = params.get("fromTask");
    if (!taskId) return;
    fromTaskProcessed.current = true;

    setFromTaskId(taskId);
    const rawTiersType = params.get("tiersType");
    const rawTiersId = params.get("tiersId") || undefined;
    const legacyClientId = params.get("clientId");
    const legacyProspectId = params.get("prospectId");
    const resolvedTiersType = rawTiersType || (legacyProspectId ? "prospect" : legacyClientId ? "client" : "prospect");
    const resolvedTiersId = rawTiersId || legacyProspectId || legacyClientId || undefined;
    setFormData(prev => ({
      ...prev,
      eventName: params.get("eventName") || "",
      description: params.get("description") || "",
      missionType: (params.get("missionType") as any) || "demonstration",
      tiersType: resolvedTiersType,
      tiersId: resolvedTiersId,
      clientId: resolvedTiersType === "client" ? resolvedTiersId || "" : "",
      prospectId: resolvedTiersType === "prospect" ? resolvedTiersId : undefined,
      commercialId: (() => {
        const value = params.get("commercialId");
        return value && profileIdSchema.safeParse(value).success ? profileIdSchema.parse(value) : profile?.id;
      })(),
    }));

    const techId = params.get("technicianId");
    if (techId) {
      setTechAssignments([{ technicianId: techId, isRemote: false }]);
    }

    setDialogOpen(true);
    window.history.replaceState({}, "", "/missions");
  }, [profile]);

  const isEdit = !!editingMission;
  const allAssignableProfiles = allProfiles?.filter((p: Profile) => p.isActive && p.role !== "admin") || [];

  useEffect(() => {
    if (!dialogOpen || !formData.startDate || !formData.endDate) {
      setAvailability(null);
      return;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          startDate: new Date(formData.startDate!).toISOString(),
          endDate: new Date(formData.endDate!).toISOString(),
        });
        if (editingMission) params.set("excludeMissionId", editingMission.id);
        if (formData.heureDebut) params.set("heureDebut", formData.heureDebut);
        if (formData.heureFin) params.set("heureFin", formData.heureFin);
        const res = await authFetch(`/api/missions/availability?${params}`, {
          signal: controller.signal,
        });
        if (res.ok) {
          const data = await res.json();
          if (!controller.signal.aborted) setAvailability(data);
        } else {
          setAvailability(null);
        }
      } catch (error) {
        if ((error as Error).name !== "AbortError") setAvailability(null);
      }
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [dialogOpen, formData.startDate, formData.endDate, formData.heureDebut, formData.heureFin, editingMission]);

  // Auto-remplissage adresseArrivee depuis l'adresse du tiers sélectionné
  useEffect(() => {
    if (!formData.tiersId || !formData.tiersType) return;
    if (formData.tiersType === 'client') {
      const c = (clients || []).find((x: Client) => x.id === formData.tiersId);
      if (c?.address) setFormData(prev => ({ ...prev, adresseArrivee: c.address || '' }));
    } else if (formData.tiersType === 'prospect') {
      const p = (prospectsList || []).find((x: Prospect) => x.id === formData.tiersId);
      const addr = (p as any)?.adresse || '';
      if (addr) setFormData(prev => ({ ...prev, adresseArrivee: addr }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.tiersId, formData.tiersType]);

  const resetForm = () => {
    setFormData({
      missionType: "demonstration",
      status: "option",
      clientId: "",
      tiersType: "prospect",
      tiersId: undefined,
      commercialId: profile?.id,
      createdBy: profile?.id,
      startDate: new Date(),
      endDate: new Date(),
      heureDebut: "",
      heureFin: "",
      arrivalTime: "09:00",
      demoStartTime: "10:00",
      eventName: "",
      description: "",
      journeeEntiere: false,
      lieuLibre: "",
      lienVisio: "",
      dateFin: undefined,
      adresseDepart: "",
      departLat: undefined,
      departLng: undefined,
      adresseArrivee: "",
      ariveeLat: undefined,
      ariveeLng: undefined,
    });
    setSelectedCommercialIds([]);
    setTechAssignments([]);
    setSelectedRobotIds([]);
    setClientRobotAssignments([]);
    setSelectedVehicleIds([]);
    setEditingMission(null);
    setAvailability(null);
    setVolet2Open(false);
  };

  const filteredMissions = useMemo(() => {
    if (!missions) return [];
    if (listPeriod === "all") return missions;
    const now = new Date();
    if (listPeriod === "this_week") {
      const from = startOfWeek(now, { weekStartsOn: 1 });
      const to = endOfWeek(now, { weekStartsOn: 1 });
      return missions.filter((m: any) => {
        const d = new Date(m.startDate);
        return d >= from && d <= to;
      });
    }
    if (listPeriod === "this_month") {
      const from = startOfMonth(now);
      const to = endOfMonth(now);
      return missions.filter((m: any) => {
        const d = new Date(m.startDate);
        return d >= from && d <= to;
      });
    }
    if (listPeriod === "custom") {
      const from = customDateFrom ? new Date(customDateFrom) : null;
      const to = customDateTo ? new Date(customDateTo + "T23:59:59") : null;
      return missions.filter((m: any) => {
        const d = new Date(m.startDate);
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      });
    }
    return missions;
  }, [missions, listPeriod, customDateFrom, customDateTo]);

  useEffect(() => {
    const missionIds = filteredMissions.map((mission: any) => mission.id);
    if (missionIds.length === 0) {
      setMissionAvailability({});
      return;
    }

    setMissionAvailability({});
    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      try {
        const response = await authFetch("/api/missions/availability/batch", {
          method: "POST",
          body: JSON.stringify({ missionIds }),
          signal: controller.signal,
        });
        if (!response.ok) {
          if (!controller.signal.aborted) setMissionAvailability({});
          return;
        }
        const data = await response.json();
        if (!controller.signal.aborted) setMissionAvailability(data.missions || {});
      } catch (error) {
        if ((error as Error).name !== "AbortError") setMissionAvailability({});
      }
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [filteredMissions]);

  const openCreate = (prefillDate?: Date) => {
    resetForm();
    if (prefillDate) {
      setFormData(prev => ({
        ...prev,
        startDate: prefillDate,
        endDate: prefillDate,
      }));
    }
    setDialogOpen(true);
  };

  const openEdit = async (mission: Mission) => {
    setLoadingMissionId(mission.id);
    try {
      const res = await authFetch(`/api/missions/${mission.id}`);
      if (!res.ok) throw new Error("Failed to load mission");
      const detail = await res.json();

      setEditingMission(mission);
      let tiersType: string = "prospect";
      let tiersId: string | undefined;
      if (detail.tiersType === 'interne') {
        tiersType = 'interne';
        tiersId = undefined;
      } else if (detail.tiersType && detail.tiersId) {
        tiersType = detail.tiersType;
        tiersId = detail.tiersId;
      } else if (detail.prospectId) {
        tiersType = "prospect";
        tiersId = detail.prospectId;
      } else if (detail.clientId) {
        tiersType = "client";
        tiersId = detail.clientId;
      }
      setFormData({
        missionType: detail.missionType,
        status: detail.status,
        clientId: detail.clientId || undefined,
        prospectId: detail.prospectId || undefined,
        tiersType,
        tiersId,
        commercialId: detail.commercialId,
        createdBy: detail.createdBy,
        startDate: new Date(detail.startDate),
        endDate: new Date(detail.endDate),
        heureDebut: detail.heureDebut || "",
        heureFin: detail.heureFin || "",
        arrivalTime: detail.arrivalTime || "",
        demoStartTime: detail.demoStartTime || "",
        eventName: detail.eventName || "",
        description: detail.description || "",
        journeeEntiere: detail.journeeEntiere || false,
        lieuLibre: detail.lieuLibre || "",
        lienVisio: detail.lienVisio || "",
        dateFin: detail.dateFin || undefined,
        adresseDepart: detail.adresseDepart || "",
        departLat: detail.departLat ?? undefined,
        departLng: detail.departLng ?? undefined,
        adresseArrivee: detail.adresseArrivee || "",
        ariveeLat: detail.ariveeLat ?? undefined,
        ariveeLng: detail.ariveeLng ?? undefined,
        dureeTrajetMinutes: detail.dureeTrajetMinutes ?? undefined,
        distanceTrajetKm: detail.distanceTrajetKm ?? undefined,
        trajetErreur: detail.trajetErreur ?? undefined,
      });
      setVolet2Open(!!(detail.tiersId || detail.robotIds?.length || detail.vehicleIds?.length || detail.commercialIds?.length));
      setTechAssignments(detail.technicianAssignments || []);
      setSelectedRobotIds(detail.robotIds || []);
      setClientRobotAssignments((detail.robotClientAssignments || detail.clientRobotAssignments || []).map((row: any) => ({
        robotClientId: row.robotClientId || row.robot_client_id,
        motif: row.motif || "",
        remplacementRequis: !!(row.remplacementRequis ?? row.remplacement_requis),
        robotRemplacementId: row.robotRemplacementId || row.robot_remplacement_id || null,
        robotRemplacementSn: row.robotRemplacementSn || row.robot_remplacement_sn || null,
      })));
      setSelectedVehicleIds(detail.vehicleIds || []);
      setSelectedCommercialIds(detail.commercialIds || []);
      setDialogOpen(true);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger la mission" });
    }
    setLoadingMissionId(null);
  };

  const openDetail = async (mission: Mission) => {
    setLoadingMissionId(mission.id);
    try {
      const res = await authFetch(`/api/missions/${mission.id}`);
      if (!res.ok) throw new Error("Failed to load mission");
      const detail = await res.json();
      setDetailMission(detail);
      setDetailOpen(true);
    } catch (e) {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de charger la mission" });
    }
    setLoadingMissionId(null);
  };

  const loadMissionDocs = async (missionId: string) => {
    setDocsLoading(true);
    setDocsError(null);
    try {
      const res = await authFetch(`/api/missions/${missionId}/documents`);
      if (!res.ok) { setDocsError("Impossible de charger les documents"); setMissionDocs([]); return; }
      setMissionDocs(await res.json());
    } catch (e: any) {
      setDocsError(e.message || "Erreur réseau");
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    if (detailOpen && detailMission?.id) {
      loadMissionDocs(detailMission.id);
    } else if (dialogOpen && editingMission?.id) {
      loadMissionDocs(editingMission.id);
    } else {
      setMissionDocs([]);
      setDocsError(null);
    }
  }, [detailOpen, detailMission?.id, dialogOpen, editingMission?.id]);

  // Résout l'ID de la mission active (détail ou édition)
  const activeMissionId = detailMission?.id || editingMission?.id || null;

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!activeMissionId) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast({ variant: "destructive", title: "Fichier trop volumineux", description: "Maximum 20 Mo" });
      return;
    }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("categorie", uploadDocCategorie);
      if (uploadDocDesc.trim()) fd.append("description", uploadDocDesc.trim());
      const res = await authFetch(`/api/missions/${activeMissionId}/documents`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur upload");
      setMissionDocs(prev => [...prev, data]);
      setUploadDocDesc("");
      toast({ title: "Document ajouté" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  const handleDocDownload = async (doc: any) => {
    if (!activeMissionId) return;
    try {
      const res = await authFetch(`/api/missions/${activeMissionId}/documents/${doc.id}/download`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Erreur download");
      const a = document.createElement("a");
      a.href = data.url;
      a.download = doc.nomFichier || "document";
      a.target = "_blank";
      a.click();
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    }
  };

  const handleDocDelete = async (docId: string) => {
    if (!activeMissionId) return;
    setDeletingDocId(docId);
    try {
      const res = await authFetch(`/api/missions/${activeMissionId}/documents/${docId}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Erreur suppression");
      }
      setMissionDocs(prev => prev.filter(d => d.id !== docId));
      toast({ title: "Document supprimé" });
    } catch (e: any) {
      toast({ variant: "destructive", title: "Erreur", description: e.message });
    } finally {
      setDeletingDocId(null);
      setConfirmDeleteDocId(null);
    }
  };

  const toggleCommercial = (id: string) => {
    setSelectedCommercialIds(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const toggleTechnician = (techId: string) => {
    setTechAssignments(prev => {
      const existing = prev.find(a => a.technicianId === techId);
      if (existing) return prev.filter(a => a.technicianId !== techId);
      return [...prev, { technicianId: techId, isRemote: false }];
    });
  };

  const toggleTechRemote = (techId: string) => {
    setTechAssignments(prev =>
      prev.map(a => a.technicianId === techId ? { ...a, isRemote: !a.isRemote } : a)
    );
  };

  const toggleRobot = (robotId: string) => {
    setSelectedRobotIds(prev =>
      prev.includes(robotId) ? prev.filter(r => r !== robotId) : [...prev, robotId]
    );
  };

  const addClientRobotAssignment = (robotClientId: string) => {
    if (clientRobotAssignments.some(row => row.robotClientId === robotClientId)) {
      toast({ variant: "destructive", title: "Robot déjà ajouté", description: "Ce robot client est déjà présent sur la mission." });
      return;
    }
    setClientRobotAssignments(prev => [...prev, {
      robotClientId, motif: "", remplacementRequis: false, robotRemplacementId: null, robotRemplacementSn: null,
    }]);
  };

  const updateClientRobotAssignment = (robotClientId: string, updates: Partial<ClientRobotAssignment>) => {
    setClientRobotAssignments(prev => prev.map(row => row.robotClientId === robotClientId ? { ...row, ...updates } : row));
  };

  const toggleVehicle = (vehicleId: string) => {
    setSelectedVehicleIds(prev =>
      prev.includes(vehicleId) ? prev.filter(v => v !== vehicleId) : [...prev, vehicleId]
    );
  };

  const showRobotLoanWarnings = (result: any) => {
    const warnings = result?.robotLoanWarnings;
    if (!Array.isArray(warnings) || warnings.length === 0) return;
    toast({
      variant: "default",
      title: "Prêts à clôturer explicitement",
      description: warnings.join(" "),
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Helper : convertit une chaîne vide ou undefined en null (pour les champs UUID optionnels)
    const uuidOrNull = (v: string | null | undefined): string | null =>
      v === "" || v === undefined ? null : v;

    try {
      const rule = MISSION_TYPES[formData.missionType || ""];
      const tt = formData.tiersType as "prospect" | "client" | "revendeur" | "interne" | undefined;
      const tid = uuidOrNull(formData.tiersId);
      const tiersFields = rule?.tiersRequis && tt && tid ? {
        tiersType: tt,
        tiersId: tid,
        prospectId: tt === "prospect" ? tid : null,
        clientId: tt === "client" ? tid : null,
      } : {
        tiersType: tt || null,
        tiersId: tid,
        prospectId: tt === "prospect" && tid ? tid : null,
        clientId: tt === "client" && tid ? tid : null,
      };
      const normalizedClientAssignments = formData.tiersType === "client"
        ? clientRobotAssignments.map(row => ({
          robotClientId: row.robotClientId,
          motif: row.motif.trim() || null,
          remplacementRequis: row.remplacementRequis,
          robotRemplacementId: row.remplacementRequis ? (row.robotRemplacementId || null) : null,
          robotRemplacementSn: row.remplacementRequis && !row.robotRemplacementId ? (row.robotRemplacementSn?.trim() || null) : null,
        }))
        : [];
      if (normalizedClientAssignments.some(row => row.remplacementRequis && !row.robotRemplacementId && !row.robotRemplacementSn)) {
        toast({ variant: "destructive", title: "Remplacement incomplet", description: "Choisissez un robot de flotte ou saisissez son numéro de série." });
        return;
      }

      // Validation dateFin >= startDate (client)
      if (formData.dateFin) {
        const startStr = format(new Date(formData.startDate!), 'yyyy-MM-dd');
        if (String(formData.dateFin) < startStr) {
          toast({ variant: "destructive", title: "Dates invalides", description: "La date de fin doit être ≥ à la date de début." });
          return;
        }
      }

      // Convention : mission journée entière → end_date = start_date (même ms).
      // Mission timée avec dateFin : fin de journée du dateFin (minuit-1ms local).
      // Mission timée sans dateFin : end_date = start_date.
      let resolvedEndDate: Date;
      if (formData.journeeEntiere) {
        resolvedEndDate = new Date(formData.startDate!);
      } else if (formData.dateFin) {
        const [y, mo, d] = String(formData.dateFin).split('-').map(Number);
        resolvedEndDate = new Date(y, mo - 1, d, 23, 59, 59, 999);
      } else {
        resolvedEndDate = new Date(formData.startDate!);
      }

      if (isEdit && editingMission) {
        const updatePayload: { id: string } & UpdateMissionRequest = {
          id: editingMission.id,
          ...formData,
          ...tiersFields,
          commercialId: uuidOrNull((formData as any).commercialId),
          startDate: new Date(formData.startDate!),
          endDate: resolvedEndDate,
          technicianAssignments: techAssignments,
          robotIds: selectedRobotIds,
          vehicleIds: selectedVehicleIds,
          commercialIds: selectedCommercialIds,
          robotClientAssignments: normalizedClientAssignments,
        } as any;
        const updatedMission = await updateMutation.mutateAsync(updatePayload);
        showRobotLoanWarnings(updatedMission);
        toast({ title: "Mission mise à jour" });
      } else {
        const newMission = await createMutation.mutateAsync({
          ...formData,
          ...tiersFields,
          startDate: new Date(formData.startDate!),
          endDate: resolvedEndDate,
          commercialId: profile!.id,
          createdBy: profile!.id,
          technicianAssignments: techAssignments.length > 0 ? techAssignments : undefined,
          robotIds: selectedRobotIds.length > 0 ? selectedRobotIds : undefined,
          vehicleIds: selectedVehicleIds.length > 0 ? selectedVehicleIds : undefined,
          commercialIds: selectedCommercialIds.length > 0 ? selectedCommercialIds : undefined,
          robotClientAssignments: normalizedClientAssignments,
        } as CreateMissionRequest);
        showRobotLoanWarnings(newMission);

        if (fromTaskId && newMission?.id) {
          try {
            await updateTaskMutation.mutateAsync({ id: fromTaskId, missionId: newMission.id });
          } catch {
            toast({ variant: "destructive", title: "Attention", description: "Mission créée mais le lien avec l'action a échoué" });
          }
          setFromTaskId(null);
        }
        toast({ title: "Mission créée" });
      }

      setDialogOpen(false);
      resetForm();
      setFromTaskId(null);
    } catch (error: any) {
      // apiRequest throw "STATUS: {json body}" — extraire le JSON pour afficher le message réel
      let description = isEdit ? "Impossible de mettre à jour la mission" : "Impossible de créer la mission";
      try {
        const msg: string = error?.message ?? "";
        const jsonStart = msg.indexOf("{");
        if (jsonStart !== -1) {
          const body = JSON.parse(msg.slice(jsonStart));
          if (body?.message) description = body.message;
        }
      } catch { /* garder le libellé de repli */ }
      toast({ variant: "destructive", title: "Erreur", description });
    }
  };

  const openLogisticTaskDialog = (mission: Mission) => {
    setTaskMission(mission);
    setTaskType("reservation_hotel");
    setTaskTitle("");
    setTaskDialogOpen(true);
  };

  const handleCreateLogisticTask = async () => {
    if (!taskMission || !taskTitle) return;
    try {
      await createTaskMutation.mutateAsync({
        titre: taskTitle,
        emetteur: profile?.firstName || "Système",
        assigneeId: profile?.id || null,
        assigneeType: "autre",
        labels: ["Logistique"],
        priorite: "2",
        status: "a_faire",
        missionId: taskMission.id,
        dateEcheance: taskMission.startDate ? new Date(taskMission.startDate).toISOString().split("T")[0] : null,
        notes: null,
        createdBy: profile?.id || null,
      });
      setTaskDialogOpen(false);
      toast({ title: "Tâche créée", description: "La tâche logistique a été créée" });
    } catch {
      toast({ variant: "destructive", title: "Erreur", description: "Impossible de créer la tâche" });
    }
  };

  const formatDate = (d: string | Date) => {
    try { return format(new Date(d), "yyyy-MM-dd"); } catch { return ""; }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold" data-testid="text-page-title">Missions</h1>
          <p className="text-muted-foreground mt-1">Planification et suivi des opérations</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-lg border bg-card p-0.5 gap-0.5">
            <Button
              variant={viewMode === "week5" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("week5")}
              className="h-7 px-2.5 text-xs gap-1.5"
              data-testid="btn-view-week5"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              Vue semaine
            </Button>
            <Button
              variant={viewMode === "week7" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("week7")}
              className="h-7 px-2.5 text-xs gap-1.5"
              data-testid="btn-view-week7"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              7 jours
            </Button>
            <Button
              variant={viewMode === "day" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("day")}
              className="h-7 px-2.5 text-xs gap-1.5"
              data-testid="btn-view-day"
            >
              <Sun className="h-3.5 w-3.5" />
              Journée
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-7 px-2.5 text-xs gap-1.5"
              data-testid="btn-view-list"
            >
              <List className="h-3.5 w-3.5" />
              Vue liste
            </Button>
          </div>
          <Button className="shadow-lg shadow-primary/20" onClick={() => openCreate()} data-testid="button-open-create-mission">
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle mission
          </Button>
        </div>
      </div>

      {viewMode === "week5" ? (
        <MissionWeekView
          weekStart={currentWeekStart}
          numDays={5}
          onWeekChange={setCurrentWeekStart}
          missions={missions || []}
          clients={clients || []}
          prospects={prospectsList || []}
          profiles={allProfiles || []}
          resellers={resellers || []}
          onMissionClick={openDetail}
          onNewMission={openCreate}
          weeklyPdfActions={
            <WeeklyPdfActions
              weekStart={currentWeekStart}
              missions={missions || []}
              canManage={userLevel >= ROLE_LEVEL.admin}
            />
          }
          onDayClick={(day) => { setCurrentDay(day); setViewMode("day"); }}
        />
      ) : viewMode === "week7" ? (
        <MissionWeekView
          weekStart={currentWeekStart}
          numDays={7}
          onWeekChange={setCurrentWeekStart}
          missions={missions || []}
          clients={clients || []}
          prospects={prospectsList || []}
          profiles={allProfiles || []}
          resellers={resellers || []}
          onMissionClick={openDetail}
          onNewMission={openCreate}
          weeklyPdfActions={
            <WeeklyPdfActions
              weekStart={currentWeekStart}
              missions={missions || []}
              canManage={userLevel >= ROLE_LEVEL.admin}
            />
          }
          onDayClick={(day) => { setCurrentDay(day); setViewMode("day"); }}
        />
      ) : viewMode === "day" ? (
        <MissionDayView
          currentDay={currentDay}
          onDayChange={setCurrentDay}
          missions={missions || []}
          clients={clients || []}
          prospects={prospectsList || []}
          profiles={allProfiles || []}
          resellers={resellers || []}
          onMissionClick={openDetail}
          onNewMission={openCreate}
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground font-medium">Période :</span>
            {(
              [
                { value: "all" as const, label: "Toutes" },
                { value: "this_week" as const, label: "Cette semaine" },
                { value: "this_month" as const, label: "Ce mois" },
                { value: "custom" as const, label: "Plage personnalisée" },
              ] as const
            ).map(opt => (
              <Button
                key={opt.value}
                variant={listPeriod === opt.value ? "secondary" : "outline"}
                size="sm"
                onClick={() => setListPeriod(opt.value)}
                className="h-7 text-xs"
                data-testid={`btn-period-${opt.value}`}
              >
                {opt.label}
              </Button>
            ))}
            {listPeriod === "custom" && (
              <div className="flex items-center gap-2 mt-1 sm:mt-0">
                <Input
                  type="date"
                  value={customDateFrom}
                  onChange={e => setCustomDateFrom(e.target.value)}
                  className="h-7 text-xs w-36"
                  data-testid="input-custom-date-from"
                />
                <span className="text-xs text-muted-foreground">→</span>
                <Input
                  type="date"
                  value={customDateTo}
                  onChange={e => setCustomDateTo(e.target.value)}
                  className="h-7 text-xs w-36"
                  data-testid="input-custom-date-to"
                />
              </div>
            )}
          </div>

          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/40">
                  <TableHead>Mission</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-center">Dispo</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center h-32">Chargement des missions...</TableCell></TableRow>
                ) : filteredMissions.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center h-32 text-muted-foreground">Aucune mission</TableCell></TableRow>
                ) : filteredMissions.map((mission: any) => {
                  const tt = mission.tiersType;
                  const tid = mission.tiersId;
                  let entityName: string | undefined;
                  if (tt === "prospect" && tid) entityName = (prospectsList as any[])?.find((p: Prospect) => p.id === tid)?.societe;
                  else if (tt === "client" && tid) { const c = (clients as any[])?.find((c: Client) => c.id === tid); entityName = c?.nomCommercial || c?.companyName; }
                  else if (tt === "revendeur" && tid) { const r = (resellers as any[])?.find((r: any) => r.id === tid); entityName = r?.nomCommercial || r?.companyName; }
                  else if (mission.prospectId) entityName = (prospectsList as any[])?.find((p: Prospect) => p.id === mission.prospectId)?.societe;
                  else if (mission.clientId) { const c = (clients as any[])?.find((c: Client) => c.id === mission.clientId); entityName = c?.nomCommercial || c?.companyName; }
                  const displayTitle = resolveMissionTitle(mission, entityName);
                  return (
                    <TableRow key={mission.id} className={`group hover:bg-secondary/20 transition-colors ${mission.isMine ? "border-l-[3px] border-l-[hsl(var(--accent-mine))]" : ""}`} data-testid={`row-mission-${mission.id}`}>
                      <TableCell>
                        <div>
                          <div className="font-semibold" title={displayTitle} data-testid={`text-mission-name-${mission.id}`}>
                            {displayTitle}
                          </div>
                          {mission.arrivalTime && (
                            <span className="text-xs text-muted-foreground">{mission.arrivalTime}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{entityName || '—'}</TableCell>
                      <TableCell className="text-sm">
                        <div>
                          {format(new Date(mission.startDate), 'dd MMM', { locale: fr })}
                          {mission.heureDebut && <span className="text-muted-foreground"> {mission.heureDebut}</span>}
                          {mission.startDate !== mission.endDate && ` - ${format(new Date(mission.endDate), 'dd MMM', { locale: fr })}`}
                          {mission.heureFin && <span className="text-muted-foreground"> {mission.heureFin}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                          {MISSION_TYPE_LABELS[mission.missionType] || mission.missionType}
                        </span>
                      </TableCell>
                      <TableCell><StatusBadge status={mission.status} type="mission" /></TableCell>
                      <TableCell className="text-center">
                        {mission.status === "option" || mission.status === "confirme" ? (
                          <MissionConflictSummary
                            mission={mission}
                            verdict={missionAvailability[mission.id]}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`button-actions-${mission.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openDetail(mission)} data-testid={`button-view-${mission.id}`}>
                              <Eye className="w-4 h-4 mr-2" />
                              Voir détails
                            </DropdownMenuItem>
                            {canEdit && (
                              <DropdownMenuItem onClick={() => openEdit(mission)} data-testid={`button-edit-${mission.id}`}>
                                <Pencil className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => openLogisticTaskDialog(mission)} data-testid={`button-add-task-${mission.id}`}>
                              <ClipboardList className="w-4 h-4 mr-2" />
                              Ajouter tâche logistique
                            </DropdownMenuItem>
                            {canDeleteMission(mission) && (
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setDeleteConfirmId(mission.id)}
                                data-testid={`button-delete-${mission.id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { resetForm(); } setDialogOpen(open); }}>
        <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isEdit ? "Modifier la mission" : "Créer une mission"}</DialogTitle>
            <DialogDescription>{isEdit ? "Modifiez les informations de la mission" : "Remplissez les informations de la nouvelle mission"}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">

            {/* ───── VOLET 1 — Calendrier & Qui ───── */}
            <div className="space-y-4">

              {/* Ligne 1 : Titre + Statut */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nom / Événement <span className="text-destructive">*</span></Label>
                  <Input value={formData.eventName || ''} onChange={e => setFormData({...formData, eventName: e.target.value})} placeholder="Ex: Démonstration COMEPA" data-testid="input-event-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Statut</Label>
                  <Select value={formData.status || "option"} onValueChange={v => setFormData({...formData, status: v as any})}>
                    <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(MISSION_STATUS_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ligne 2 : Type */}
              <div className="space-y-1.5">
                <Label>Type <span className="text-destructive">*</span></Label>
                <Select
                  value={formData.missionType || "demonstration"}
                  onValueChange={v => {
                    const newRule = MISSION_TYPES[v];
                    const currentTiersType = formData.tiersType as string;
                    const tiersReset = newRule && newRule.tiersNatures.length > 0 && !newRule.tiersNatures.includes(currentTiersType as any);
                    setFormData(prev => ({
                      ...prev,
                      missionType: v as any,
                      ...(tiersReset ? { tiersType: newRule.tiersNatures[0], tiersId: undefined, clientId: undefined, prospectId: undefined } : {}),
                    }));
                  }}
                >
                  <SelectTrigger data-testid="select-mission-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.values(MISSION_TYPES).map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ligne 3 : Concernés (requis) */}
              {allAssignableProfiles.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Concernés <span className="text-destructive">*</span></Label>
                  <MultiBadgeSelect
                    options={allAssignableProfiles.map((p: Profile) => ({
                      value: p.id,
                      label: `${p.firstName} ${p.lastName}`,
                      sublabel: p.role ? p.role.charAt(0).toUpperCase() + p.role.slice(1) : undefined,
                      badgeContent: <AvailabilityBadge resourceId={p.id} type="technicians" availability={availability} />,
                    } as MultiBadgeOption))}
                    value={techAssignments.map(a => a.technicianId)}
                    onChange={(ids) => {
                      setTechAssignments(prev => {
                        const kept = prev.filter(a => ids.includes(a.technicianId));
                        const added = ids.filter(id => !prev.find(a => a.technicianId === id))
                          .map(id => ({ technicianId: id, isRemote: false }));
                        return [...kept, ...added];
                      });
                    }}
                    placeholder="Ajouter un membre de l'équipe..."
                    emptyText="Aucun profil trouvé"
                    data-testid="multi-select-technicians"
                  />
                  {techAssignments.length > 0 && (
                    <div className="space-y-1 pt-1">
                      {techAssignments.map(a => {
                        const p = allAssignableProfiles.find((pr: Profile) => pr.id === a.technicianId);
                        if (!p) return null;
                        return (
                          <label key={a.technicianId} className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid={`checkbox-remote-${p.id}`}>
                            <Checkbox checked={a.isRemote || false} onCheckedChange={() => toggleTechRemote(p.id)} />
                            <span>{p.firstName} {p.lastName} — À distance</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Ligne 4 : Journée entière */}
              <div className="flex items-center gap-3 py-0.5">
                <Switch
                  id="journee-entiere"
                  checked={!!formData.journeeEntiere}
                  onCheckedChange={v => setFormData({...formData, journeeEntiere: v})}
                  data-testid="switch-journee-entiere"
                />
                <Label htmlFor="journee-entiere" className="cursor-pointer font-normal">Journée entière</Label>
              </div>

              {/* Ligne 5 : Dates + Heures */}
              {(() => {
                const rule = MISSION_TYPES[formData.missionType || ""];
                const dateFinReq = rule?.dateFinRequise;
                const showHeures = !formData.journeeEntiere;
                const cols = showHeures ? "1fr 1fr 1fr 1fr" : "1fr 1fr";
                return (
                  <div className="grid gap-4" style={{ gridTemplateColumns: cols }}>
                    <div className="space-y-1.5">
                      <Label>Date début <span className="text-destructive">*</span></Label>
                      <Input type="date" value={formatDate(formData.startDate!)} onChange={e => setFormData({...formData, startDate: new Date(e.target.value)})} data-testid="input-start-date" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Date fin {dateFinReq && <span className="text-destructive">*</span>}</Label>
                      <Input
                        type="date"
                        value={formData.dateFin ? String(formData.dateFin) : ''}
                        min={formatDate(formData.startDate!)}
                        onChange={e => setFormData({...formData, dateFin: e.target.value || undefined})}
                        data-testid="input-date-fin"
                      />
                    </div>
                    {showHeures && (
                      <>
                        <div className="space-y-1.5">
                          <Label>Heure début</Label>
                          <Input type="time" value={formData.heureDebut || ''} onChange={e => setFormData({...formData, heureDebut: e.target.value})} data-testid="input-heure-debut" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Heure fin</Label>
                          <Input type="time" value={formData.heureFin || ''} onChange={e => setFormData({...formData, heureFin: e.target.value})} data-testid="input-heure-fin" />
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}

              {/* Ligne 6 : Lieu */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Navigation className="h-3.5 w-3.5 text-muted-foreground" />Lieu</Label>
                <AddressAutocomplete
                  value={formData.lieuLibre || ''}
                  onChange={v => setFormData({...formData, lieuLibre: v})}
                  onSelect={r => setFormData({...formData, lieuLibre: r.label})}
                  placeholder="Adresse ou description du lieu..."
                  data-testid="input-lieu-libre"
                />
              </div>

              {/* Ligne 7 : Description */}
              <div className="space-y-1.5">
                <Label>Description / Notes</Label>
                <Textarea value={formData.description || ''} onChange={e => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Notes, informations logistiques..." data-testid="input-description" />
              </div>

              {/* Ligne 8 : Lien visio */}
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5"><Video className="h-3.5 w-3.5 text-muted-foreground" />Lien visio</Label>
                <Input
                  type="url"
                  value={formData.lienVisio || ''}
                  onChange={e => setFormData({...formData, lienVisio: e.target.value})}
                  placeholder="https://meet.google.com/..."
                  data-testid="input-lien-visio"
                />
              </div>
            </div>

            {/* ───── VOLET 2 — Détails opérationnels (disclosure) ───── */}
            {(() => {
              const rule = MISSION_TYPES[formData.missionType || ""];
              const showTiers = rule && rule.tiersNatures.length > 0;
              const showRobots = allRobots && allRobots.length > 0;
              const showVehicles = allVehicles && allVehicles.length > 0;
              const showCommercials = commercials && commercials.length > 0;
              const showSiteDemo = rule?.champsSiteDemo;
              const hasContent = showTiers || showRobots || showVehicles || showCommercials || showSiteDemo;
              if (!hasContent) return null;

              const tiersLabel = rule?.tiersRequis ? (
                <span>Tiers <span className="text-destructive">*</span></span>
              ) : "Tiers";

              // Compute badge: count of filled operational fields
              const badge = [
                formData.tiersId ? 1 : 0,
                selectedRobotIds.length,
                selectedVehicleIds.length,
                selectedCommercialIds.length,
              ].reduce((a, b) => a + b, 0);

              return (
                <div className="border rounded-lg overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-4 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-sm font-medium"
                    onClick={() => setVolet2Open(v => !v)}
                    data-testid="toggle-volet2"
                  >
                    <span className="flex items-center gap-2">
                      Détails opérationnels
                      {badge > 0 && <span className="inline-flex items-center justify-center h-4.5 px-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold min-w-[18px]">{badge}</span>}
                    </span>
                    {volet2Open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  </button>

                  {volet2Open && (
                    <div className="p-4 space-y-4">

                      {/* Tiers */}
                      {showTiers && (
                        <div className="space-y-1.5">
                          <Label>{tiersLabel}</Label>
                          <div className="flex rounded-lg border bg-muted/30 p-0.5 gap-0.5 mb-2">
                            {(rule!.tiersNatures as string[]).map(nat => (
                              <button
                                key={nat}
                                type="button"
                                className={`flex-1 text-xs font-medium py-1 px-2 rounded-md transition-all ${
                                  formData.tiersType === nat
                                    ? "bg-background shadow-sm text-foreground"
                                    : "text-muted-foreground hover:text-foreground"
                                }`}
                                onClick={() => {
                                  if (formData.tiersType !== nat) {
                                    setFormData({ ...formData, tiersType: nat, tiersId: undefined, clientId: undefined, prospectId: undefined });
                                  }
                                }}
                                data-testid={`pill-tiers-${nat}`}
                              >
                                {{ prospect: "Prospect", client: "Client", revendeur: "Revendeur", interne: "Interne (Jobotto)" }[nat] || nat}
                              </button>
                            ))}
                          </div>
                          {formData.tiersType === "prospect" && (
                            <SearchSelect
                              value={formData.tiersId || null}
                              onValueChange={v => setFormData({ ...formData, tiersId: v || undefined })}
                              options={(prospectsList?.filter((p: Prospect) => p.actif !== false) || []).map((p: Prospect) => ({
                                value: p.id, label: p.societe,
                                sublabel: [p.codePostal, p.ville].filter(Boolean).join(" ") || undefined,
                              }))}
                              placeholder="Sélectionner un prospect"
                              emptyText="Aucun prospect trouvé"
                              data-testid="select-tiers"
                            />
                          )}
                          {formData.tiersType === "client" && (
                            <SearchSelect
                              value={formData.tiersId || null}
                              onValueChange={v => setFormData({ ...formData, tiersId: v || undefined })}
                              options={(clients || []).map((c: Client) => ({
                                value: c.id, label: (c as any).nomCommercial || c.companyName,
                                sublabel: extractLocation(c.address) || c.sector || undefined,
                              }))}
                              placeholder="Sélectionner un client"
                              emptyText="Aucun client trouvé"
                              data-testid="select-tiers"
                            />
                          )}
                          {formData.tiersType === "revendeur" && (
                            <SearchSelect
                              value={formData.tiersId || null}
                              onValueChange={v => setFormData({ ...formData, tiersId: v || undefined })}
                              options={(resellers || []).map((r: any) => ({
                                value: r.id, label: r.nomCommercial || r.companyName,
                                sublabel: r.contractStatus || undefined,
                              }))}
                              placeholder="Sélectionner un revendeur"
                              emptyText="Aucun revendeur trouvé"
                              data-testid="select-tiers"
                            />
                          )}
                        </div>
                      )}

                      {/* Robots du client : intervention et remplacement, séparés de la flotte */}
                      {formData.tiersType === "client" && !!formData.tiersId && (
                        <div className="space-y-2 rounded-lg border border-primary/20 bg-primary/[0.03] p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <Label>Robots du client</Label>
                              <p className="text-xs text-muted-foreground mt-0.5">Un motif par robot examiné. Les remplacements vivent indépendamment de cette mission.</p>
                            </div>
                            <Select value="" onValueChange={addClientRobotAssignment}>
                              <SelectTrigger className="h-8 w-[190px] text-xs" data-testid="select-add-client-robot">
                                <SelectValue placeholder="Ajouter un robot" />
                              </SelectTrigger>
                              <SelectContent>
                                {(clientRobots || []).filter((r: any) => !clientRobotAssignments.some(a => a.robotClientId === r.id)).map((r: any) => (
                                  <SelectItem key={r.id} value={r.id}>{r.modele || r.modelName || "Robot"} · {r.numeroSerie || r.serialNumber || "SN inconnu"}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {clientRobotAssignments.length === 0 ? (
                            <p className="rounded border border-dashed px-3 py-3 text-xs text-muted-foreground">Aucun robot client ajouté à cette mission.</p>
                          ) : (
                            <div className="space-y-2">
                              {clientRobotAssignments.map(row => {
                                const clientRobot = (clientRobots || []).find((r: any) => r.id === row.robotClientId);
                                const selectedFleetRobot = (allRobots || []).find((r: any) => r.id === row.robotRemplacementId);
                                const availableFleet = (availableReplacementRobots || []).filter((r: any) =>
                                  isRobotFleetAvailable(r.status) && r.id !== row.robotRemplacementId
                                );
                                return (
                                  <div key={row.robotClientId} className="rounded-md border bg-background p-3 space-y-2" data-testid={`client-robot-assignment-${row.robotClientId}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className="text-sm font-medium truncate">{clientRobot?.modele || clientRobot?.modelName || "Robot client"}</p>
                                        <p className="text-[11px] text-muted-foreground font-mono">{clientRobot?.numeroSerie || clientRobot?.serialNumber || row.robotClientId}</p>
                                      </div>
                                      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs text-destructive" onClick={() => setClientRobotAssignments(prev => prev.filter(a => a.robotClientId !== row.robotClientId))}>Retirer</Button>
                                    </div>
                                    <Input className="h-8 text-xs" placeholder="Motif / symptôme constaté" value={row.motif} onChange={e => updateClientRobotAssignment(row.robotClientId, { motif: e.target.value })} data-testid={`input-client-robot-motif-${row.robotClientId}`} />
                                    <label className="flex items-center gap-2 text-xs cursor-pointer">
                                      <Checkbox checked={row.remplacementRequis} onCheckedChange={checked => updateClientRobotAssignment(row.robotClientId, { remplacementRequis: checked === true, robotRemplacementId: checked === true ? row.robotRemplacementId : null, robotRemplacementSn: checked === true ? row.robotRemplacementSn : null })} />
                                      Remplacement requis
                                    </label>
                                    {row.remplacementRequis && (
                                      <div className="grid gap-2 sm:grid-cols-2 pl-6">
                                        <Select value={row.robotRemplacementId || "serial"} onValueChange={value => updateClientRobotAssignment(row.robotClientId, { robotRemplacementId: value === "serial" ? null : value, robotRemplacementSn: value === "serial" ? row.robotRemplacementSn : null })}>
                                          <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Robot de la flotte" /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="serial">Numéro de série libre</SelectItem>
                                            {availableFleet.map((r: any) => <SelectItem key={r.id} value={r.id}>{r.modelName || r.productModel} · {r.serialNumber}</SelectItem>)}
                                          </SelectContent>
                                        </Select>
                                        {!row.robotRemplacementId ? (
                                          <Input className="h-8 text-xs font-mono" placeholder="SN du remplacement" value={row.robotRemplacementSn || ""} onChange={e => updateClientRobotAssignment(row.robotClientId, { robotRemplacementSn: e.target.value })} />
                                        ) : (
                                          <p className="text-[11px] text-muted-foreground self-center">{selectedFleetRobot?.serialNumber} sera indisponible pendant le prêt.</p>
                                        )}
                                      </div>
                                    )}
                                    {row.remplacementRequis && !row.robotRemplacementId && row.robotRemplacementSn && (
                                      <p className="pl-6 text-[11px] text-amber-700">Ce numéro n’ouvre pas de prêt. Créez la fiche du robot ensuite.</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Robots de flotte : ressource de mission, jamais les robots client */}
                      {showRobots && (() => {
                        const robotsSource = allRobots || [];
                        const robotOptions: MultiBadgeOption[] = robotsSource.map((r: any) => {
                              const colorPart = r.color ? ` · ${r.color}` : "";
                              const snLast4   = r.serialNumber ? `…${String(r.serialNumber).slice(-4)}` : undefined;
                              const sublabelParts = [r.nickname, snLast4].filter(Boolean);
                              return {
                                value: r.id,
                                image: r.imageUrl ?? null,
                                label: `${r.modelName}${colorPart}`,
                                sublabel: sublabelParts.length > 0 ? sublabelParts.join("  ·  ") : undefined,
                                searchText: [r.modelName, r.color, r.nickname, r.serialNumber].filter(Boolean).join(" "),
                                badgeContent: <AvailabilityBadge resourceId={r.id} type="robots" availability={availability} />,
                              };
                            });
                        const emptyMsg = "Aucun robot de flotte trouvé";
                        return (
                          <div className="space-y-1.5">
                            <Label>
                              {MISSION_TYPES[formData.missionType || ""]?.robotsRequis
                                ? <span>Robots <span className="text-destructive">*</span></span>
                                : "Robots"}
                               <span className="ml-1.5 text-xs font-normal text-muted-foreground">(flotte)</span>
                            </Label>
                            <MultiBadgeSelect
                              options={robotOptions}
                              value={selectedRobotIds}
                              onChange={setSelectedRobotIds}
                              placeholder="Ajouter un robot..."
                              emptyText={emptyMsg}
                              data-testid="multi-select-robots"
                            />
                          </div>
                        );
                      })()}

                      {/* Véhicules */}
                      {showVehicles && (
                        <div className="space-y-1.5">
                          <Label>{MISSION_TYPES[formData.missionType || ""]?.vehiculeRequis ? <span>Véhicules <span className="text-destructive">*</span></span> : "Véhicules"}</Label>
                          <MultiBadgeSelect
                            options={allVehicles.map((v: Vehicle) => ({
                              value: v.id,
                              label: v.brandModel,
                              sublabel: v.licensePlate || undefined,
                              badgeContent: v.nature === "parc"
                                ? <AvailabilityBadge resourceId={v.id} type="vehicles" availability={availability} />
                                : undefined,
                            } as MultiBadgeOption))}
                            value={selectedVehicleIds}
                            onChange={setSelectedVehicleIds}
                            placeholder="Ajouter un véhicule..."
                            emptyText="Aucun véhicule trouvé"
                            data-testid="multi-select-vehicles"
                          />
                        </div>
                      )}

                      {/* Commerciaux */}
                      {showCommercials && (
                        <div className="space-y-1.5">
                          <Label>Commerciaux en accompagnement</Label>
                          <MultiBadgeSelect
                            options={commercials.map((c: Profile) => ({
                              value: c.id,
                              label: `${c.firstName} ${c.lastName}`,
                              badgeContent: <AvailabilityBadge resourceId={c.id} type="commercials" availability={availability} />,
                            } as MultiBadgeOption))}
                            value={selectedCommercialIds}
                            onChange={setSelectedCommercialIds}
                            placeholder="Ajouter un commercial..."
                            emptyText="Aucun commercial trouvé"
                            data-testid="multi-select-commercials"
                          />
                        </div>
                      )}

                      {/* ─── Trajet (déplacement) ─── */}
                      {rule?.avecDeplacement && (
                        <div className="space-y-3 border-t pt-3">
                          <p className="flex items-center gap-1.5 text-sm font-medium">
                            <Car className="h-3.5 w-3.5 text-muted-foreground" />
                            Trajet
                          </p>
                          <div className="space-y-2.5">
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground">Départ</Label>
                              <AddressAutocomplete
                                value={formData.adresseDepart || ''}
                                onChange={v => setFormData(prev => ({ ...prev, adresseDepart: v, departLat: undefined, departLng: undefined }))}
                                onSelect={r => setFormData(prev => ({ ...prev, adresseDepart: r.label, departLat: r.latitude ?? undefined, departLng: r.longitude ?? undefined }))}
                                placeholder="Adresse de départ..."
                                data-testid="input-adresse-depart"
                              />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs text-muted-foreground">Arrivée</Label>
                              <AddressAutocomplete
                                value={formData.adresseArrivee || ''}
                                onChange={v => setFormData(prev => ({ ...prev, adresseArrivee: v, ariveeLat: undefined, ariveeLng: undefined }))}
                                onSelect={r => setFormData(prev => ({ ...prev, adresseArrivee: r.label, ariveeLat: r.latitude ?? undefined, ariveeLng: r.longitude ?? undefined }))}
                                placeholder="Adresse d'arrivée..."
                                data-testid="input-adresse-arrivee"
                              />
                            </div>
                          </div>
                          {/* Résultat IGN (lecture seule après sauvegarde) */}
                          {(formData.dureeTrajetMinutes || formData.distanceTrajetKm) && (
                            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2">
                              {formData.dureeTrajetMinutes != null && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {Math.floor(Number(formData.dureeTrajetMinutes) / 60) > 0 && `${Math.floor(Number(formData.dureeTrajetMinutes) / 60)}h `}
                                  {Number(formData.dureeTrajetMinutes) % 60} min
                                </span>
                              )}
                              {formData.distanceTrajetKm != null && (
                                <span className="flex items-center gap-1">
                                  <Navigation className="h-3 w-3" />
                                  {Number(formData.distanceTrajetKm).toFixed(1)} km
                                </span>
                              )}
                              {(formData as any).trajetErreur && (
                                <span className="text-destructive">{(formData as any).trajetErreur}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Champs site / démo */}
                      {showSiteDemo && (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <Label>Heure d'arrivée sur site</Label>
                            <Input type="time" value={formData.arrivalTime || ''} onChange={e => setFormData({...formData, arrivalTime: e.target.value})} data-testid="input-arrival-time" />
                            <p className="text-xs text-muted-foreground">Avant le début de la mission</p>
                          </div>
                          <div className="space-y-1.5">
                            <Label>Heure début démo</Label>
                            <Input type="time" value={formData.demoStartTime || ''} onChange={e => setFormData({...formData, demoStartTime: e.target.value})} data-testid="input-demo-start-time" />
                            <p className="text-xs text-muted-foreground">Heure de début client</p>
                          </div>
                        </div>
                      )}

                      {/* Segments (enrichi) */}
                      {ENRICHED_TYPES.includes(formData.missionType || "") && isEdit && editingMission && (
                        <div className="border-t pt-4">
                          <SegmentEditor
                            missionId={editingMission.id}
                            allProfiles={allProfiles || []}
                            allVehicles={allVehicles || []}
                            techAssignments={techAssignments}
                          />
                        </div>
                      )}
                      {ENRICHED_TYPES.includes(formData.missionType || "") && !isEdit && (
                        <p className="text-xs text-muted-foreground border-t pt-3">Le planning détaillé (segments) pourra être ajouté après la création, via "Modifier".</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── Pièces jointes (mode édition seulement) ── */}
            {isEdit && editingMission && (
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  <Paperclip className="h-4 w-4" />
                  Pièces jointes
                  {missionDocs.length > 0 && (
                    <span className="inline-flex items-center justify-center h-4 px-1.5 rounded-full bg-muted text-[10px] font-bold">
                      {missionDocs.length}
                    </span>
                  )}
                </p>

                {docsLoading && <p className="text-xs text-muted-foreground">Chargement…</p>}
                {docsError && <p className="text-xs text-destructive">{docsError}</p>}

                {!docsLoading && missionDocs.length === 0 && !docsError && (
                  <p className="text-xs text-muted-foreground italic">Aucun document attaché</p>
                )}

                {!docsLoading && missionDocs.length > 0 && (
                  <div className="space-y-1.5">
                    {missionDocs.map((doc: any) => (
                      <div key={doc.id} className="flex items-center gap-2 border rounded-md px-3 py-2 bg-muted/20" data-testid={`edit-doc-${doc.id}`}>
                        <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{doc.nomFichier}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {DOC_CATEGORIE_LABELS[doc.categorie] || doc.categorie}
                            {" · "}
                            {doc.tailleOctets < 1024 * 1024
                              ? `${(doc.tailleOctets / 1024).toFixed(0)} Ko`
                              : `${(doc.tailleOctets / 1024 / 1024).toFixed(1)} Mo`}
                            {doc.description && ` · ${doc.description}`}
                          </p>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleDocDownload(doc)}
                          title="Télécharger"
                          data-testid={`button-edit-doc-download-${doc.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        {canEdit && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive"
                            onClick={() => setConfirmDeleteDocId(doc.id)}
                            disabled={deletingDocId === doc.id}
                            title="Supprimer"
                            data-testid={`button-edit-doc-delete-${doc.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {confirmDeleteDocId && (
                  <div className="border border-destructive/30 rounded-md p-3 bg-destructive/5 space-y-2">
                    <p className="text-xs font-medium text-destructive">Supprimer ce document ?</p>
                    <div className="flex gap-2">
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setConfirmDeleteDocId(null)}>Annuler</Button>
                      <Button type="button" variant="destructive" size="sm" className="h-7 text-xs" onClick={() => handleDocDelete(confirmDeleteDocId)} disabled={!!deletingDocId}>
                        {deletingDocId ? "Suppression…" : "Supprimer"}
                      </Button>
                    </div>
                  </div>
                )}

                {canEdit && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Select value={uploadDocCategorie} onValueChange={setUploadDocCategorie}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid="select-edit-doc-categorie">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DOC_CATEGORIE_LABELS).map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input
                        className="h-8 text-xs flex-[2] border rounded-md px-2 bg-background"
                        placeholder="Description (optionnel)"
                        value={uploadDocDesc}
                        onChange={e => setUploadDocDesc(e.target.value)}
                        data-testid="input-edit-doc-description"
                      />
                    </div>
                    <div>
                      <input
                        ref={docInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.zip"
                        onChange={handleDocUpload}
                        data-testid="input-edit-doc-file"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs gap-1.5"
                        onClick={() => docInputRef.current?.click()}
                        disabled={uploadingDoc}
                        data-testid="button-edit-doc-upload"
                      >
                        {uploadingDoc ? <>Envoi en cours…</> : <><Upload className="h-3.5 w-3.5" />Ajouter un document (max 20 Mo)</>}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="pt-3 flex justify-end gap-2">
              <Button variant="outline" type="button" onClick={() => { resetForm(); setDialogOpen(false); }}>Annuler</Button>
              <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending} data-testid="button-submit-mission">
                {(createMutation.isPending || updateMutation.isPending) ? "Enregistrement..." : isEdit ? "Enregistrer" : "Créer la mission"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails de la mission</DialogTitle>
            <DialogDescription>
              {detailMission?.eventName || MISSION_TYPE_LABELS[detailMission?.missionType] || ""}
            </DialogDescription>
          </DialogHeader>
          {detailMission && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">
                    {detailMission.tiersType === "interne" ? "Nature" : detailMission.tiersType === "revendeur" ? "Revendeur" : detailMission.tiersType === "client" || (!detailMission.tiersType && detailMission.clientId) ? "Client" : "Prospect"}
                  </p>
                  <p className="font-medium">
                    {(() => {
                      const tt = detailMission.tiersType;
                      const tid = detailMission.tiersId;
                      if (tt === "interne") return "Interne (Jobotto)";
                      if (tt === "prospect" && tid) return prospectsList?.find((p: Prospect) => p.id === tid)?.societe || '—';
                      if (tt === "client" && tid) { const c = clients?.find((c: Client) => c.id === tid); return (c as any)?.nomCommercial || c?.companyName || '—'; }
                      if (tt === "revendeur" && tid) { const r = (resellers as any[])?.find((r: any) => r.id === tid); return r?.nomCommercial || r?.companyName || '—'; }
                      if (detailMission.prospectId) return prospectsList?.find((p: Prospect) => p.id === detailMission.prospectId)?.societe || '—';
                      if (detailMission.clientId) { const c = clients?.find((c: Client) => c.id === detailMission.clientId); return (c as any)?.nomCommercial || c?.companyName || '—'; }
                      return '—';
                    })()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="font-medium">{MISSION_TYPE_LABELS[detailMission.missionType]}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Date</p>
                  <p className="font-medium">
                    {format(new Date(detailMission.startDate), 'dd MMMM yyyy', { locale: fr })}
                    {detailMission.heureDebut && ` à ${detailMission.heureDebut}`}
                  </p>
                  {detailMission.endDate && new Date(detailMission.endDate).toDateString() !== new Date(detailMission.startDate).toDateString() && (
                    <p className="text-xs text-muted-foreground mt-0.5">
                      → {format(new Date(detailMission.endDate), 'dd MMMM yyyy', { locale: fr })}
                      {detailMission.heureFin && ` à ${detailMission.heureFin}`}
                    </p>
                  )}
                  {detailMission.endDate && new Date(detailMission.endDate).toDateString() === new Date(detailMission.startDate).toDateString() && detailMission.heureFin && (
                    <p className="text-xs text-muted-foreground mt-0.5">→ {detailMission.heureFin}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Statut</p>
                  <StatusBadge status={detailMission.status} type="mission" />
                </div>
              </div>
              {(detailMission.arrivalTime || detailMission.heureDebut || detailMission.heureFin) && (
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Heure d'arrivée sur site</p>
                    <p className="font-medium">{detailMission.arrivalTime || <span className="text-muted-foreground/40">—</span>}</p>
                    {detailMission.arrivalTime && <p className="text-xs text-muted-foreground/60 mt-0.5">Préparation avant mission</p>}
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Heure de début de mission</p>
                    <p className="font-medium">{detailMission.heureDebut || <span className="text-muted-foreground/40">—</span>}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Heure de fin de mission</p>
                    <p className="font-medium">{detailMission.heureFin || <span className="text-muted-foreground/40">—</span>}</p>
                  </div>
                </div>
              )}
              {detailMission.description && (
                <div>
                  <p className="text-xs text-muted-foreground">Description</p>
                  <p className="text-sm whitespace-pre-wrap bg-secondary/30 rounded-lg p-3" onCopy={handlePlainTextCopy}>{detailMission.description}</p>
                </div>
              )}
              {ENRICHED_TYPES.includes(detailMission.missionType) && (
                <div className="border-t pt-3">
                  <p className="text-xs text-muted-foreground mb-2 font-semibold">Planning détaillé</p>
                  {detailMission.segments?.length > 0 ? (
                    <SegmentTimeline segments={detailMission.segments} allProfiles={allProfiles || []} allVehicles={allVehicles || []} />
                  ) : (
                    <p className="text-xs text-muted-foreground italic">Aucun segment planifié — modifier la mission pour ajouter des segments</p>
                  )}
                </div>
              )}
              {detailMission.technicianAssignments?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Équipe</p>
                  <div className="flex flex-wrap gap-2">
                    {detailMission.technicianAssignments.map((ta: any) => {
                      const p = allProfiles?.find((pr: Profile) => pr.id === ta.technicianId);
                      return p ? (
                        <span key={ta.technicianId} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary border">
                          {p.firstName} {p.lastName}
                          {ta.isRemote && <span className="ml-1 text-muted-foreground">(à distance)</span>}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              {detailMission.robotIds?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Robots</p>
                  <div className="flex flex-wrap gap-2">
                    {detailMission.robotIds.map((rid: string) => {
                      const r = allRobots?.find((rb: any) => rb.id === rid);
                      return r ? (
                        <span key={rid} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary border">
                          {r.nickname || r.modelName}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              {detailMission.vehicleIds?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Véhicules</p>
                  <div className="flex flex-wrap gap-2">
                    {detailMission.vehicleIds.map((vid: string) => {
                      const v = allVehicles?.find((vh: Vehicle) => vh.id === vid);
                      return v ? (
                        <span key={vid} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary border">
                          {v.brandModel} ({v.licensePlate})
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              {detailMission.commercialIds?.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Commerciaux</p>
                  <div className="flex flex-wrap gap-2">
                    {detailMission.commercialIds.map((cid: string) => {
                      const c = allProfiles?.find((pr: Profile) => pr.id === cid);
                      return c ? (
                        <span key={cid} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-secondary border">
                          {c.firstName} {c.lastName}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* ── Pièces jointes ── */}
              <div className="border-t pt-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                    <Paperclip className="h-3.5 w-3.5" />
                    Pièces jointes
                    {missionDocs.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center h-4 px-1.5 rounded-full bg-muted text-[10px] font-bold">
                        {missionDocs.length}
                      </span>
                    )}
                  </p>
                </div>

                {docsLoading && <p className="text-xs text-muted-foreground py-2">Chargement…</p>}
                {docsError && <p className="text-xs text-destructive py-2">{docsError}</p>}

                {!docsLoading && !docsError && missionDocs.length === 0 && (
                  <p className="text-xs text-muted-foreground italic py-1">Aucun document attaché</p>
                )}

                {!docsLoading && missionDocs.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {missionDocs.map((doc: any) => (
                      <div key={doc.id} className="flex items-center gap-2 border rounded-md px-3 py-2 bg-muted/20" data-testid={`mission-doc-${doc.id}`}>
                        <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{doc.nomFichier}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {DOC_CATEGORIE_LABELS[doc.categorie] || doc.categorie}
                            {" · "}
                            {doc.tailleOctets < 1024 * 1024
                              ? `${(doc.tailleOctets / 1024).toFixed(0)} Ko`
                              : `${(doc.tailleOctets / 1024 / 1024).toFixed(1)} Mo`}
                            {doc.description && ` · ${doc.description}`}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleDocDownload(doc)}
                          title="Télécharger"
                          data-testid={`button-doc-download-${doc.id}`}
                        >
                          <Download className="h-3.5 w-3.5" />
                        </Button>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-destructive"
                            onClick={() => setConfirmDeleteDocId(doc.id)}
                            disabled={deletingDocId === doc.id}
                            title="Supprimer"
                            data-testid={`button-doc-delete-${doc.id}`}
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {canEdit && (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Select value={uploadDocCategorie} onValueChange={setUploadDocCategorie}>
                        <SelectTrigger className="h-8 text-xs flex-1" data-testid="select-doc-categorie">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(DOC_CATEGORIE_LABELS).map(([val, label]) => (
                            <SelectItem key={val} value={val}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <input
                        className="h-8 text-xs flex-[2] border rounded-md px-2 bg-background"
                        placeholder="Description (optionnel)"
                        value={uploadDocDesc}
                        onChange={e => setUploadDocDesc(e.target.value)}
                        data-testid="input-doc-description"
                      />
                    </div>
                    <div>
                      <input
                        ref={docInputRef}
                        type="file"
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp,.txt,.csv,.zip"
                        onChange={handleDocUpload}
                        data-testid="input-doc-file"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full h-8 text-xs gap-1.5"
                        onClick={() => docInputRef.current?.click()}
                        disabled={uploadingDoc}
                        data-testid="button-doc-upload"
                      >
                        {uploadingDoc ? (
                          <>Envoi en cours…</>
                        ) : (
                          <><Upload className="h-3.5 w-3.5" />Ajouter un document (max 20 Mo)</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {confirmDeleteDocId && (
                  <div className="mt-2 border border-destructive/30 rounded-md p-3 bg-destructive/5 space-y-2">
                    <p className="text-xs font-medium text-destructive">Supprimer ce document ?</p>
                    <p className="text-xs text-muted-foreground">Cette action est irréversible.</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setConfirmDeleteDocId(null)}>Annuler</Button>
                      <Button variant="destructive" size="sm" className="h-7 text-xs" onClick={() => handleDocDelete(confirmDeleteDocId)} disabled={!!deletingDocId}>
                        {deletingDocId ? "Suppression…" : "Supprimer"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* ── Historique des transitions de statut ──────────────────────── */}
              {entityEventsData.length > 0 && (
                <div className="pt-4 border-t" data-testid="mission-history-section">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Historique</h4>
                  <ol className="relative border-l border-border ml-2 space-y-3" data-testid="mission-history-timeline">
                    {entityEventsData.map((ev: any, idx: number) => {
                      const fromLabel = ev.fromState ? (MISSION_STATUS_LABELS[ev.fromState] ?? ev.fromState) : null;
                      const toLabel = MISSION_STATUS_LABELS[ev.toState] ?? ev.toState;
                      const actorName = ev.actorPrenom || ev.actorNom
                        ? [ev.actorPrenom, ev.actorNom].filter(Boolean).join(" ")
                        : ev.source === "system" ? "Système" : "—";
                      const dateStr = ev.createdAt
                        ? format(new Date(ev.createdAt), "dd/MM/yyyy à HH:mm", { locale: fr })
                        : "";
                      return (
                        <li key={ev.id ?? idx} className="ml-4" data-testid={`history-event-${ev.id}`}>
                          <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full border border-background bg-muted-foreground/40" />
                          <p className="text-xs leading-snug">
                            {fromLabel ? (
                              <span className="text-muted-foreground">{fromLabel} → </span>
                            ) : (
                              <span className="text-muted-foreground">Création → </span>
                            )}
                            <span className="font-medium text-foreground">{toLabel}</span>
                          </p>
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5">
                            {dateStr}{actorName !== "—" && ` · ${actorName}`}
                            {ev.source === "system" && <span className="ml-1 italic">(auto)</span>}
                          </p>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              )}
            </div>
          )}
          {detailMission && (() => {
            const isAssignedTech = detailMission?.technicianAssignments?.some((ta: any) => ta.technicianId === profile?.id);
            const canMarkRealisee = detailMission?.status === 'confirme' && (canEdit || isAssignedTech);
            const showFooter = canEdit || canDeleteMission(detailMission) || canMarkRealisee;
            if (!showFooter) return null;
            return (
              <DialogFooter className="flex gap-2 pt-2 border-t mt-2">
                {canMarkRealisee && (
                  <Button
                    size="sm"
                    className="bg-green-600 hover:bg-green-700 text-white"
                    onClick={async () => {
                      try {
                        await authFetch(`/api/missions/${detailMission.id}/marquer-realisee`, { method: 'POST' });
                        queryClient.invalidateQueries({ queryKey: ['/api/missions'] });
                        queryClient.invalidateQueries({ queryKey: ['/api/missions', detailMission.id] });
                        setDetailMission((prev: any) => prev ? { ...prev, status: 'realisee' } : prev);
                        toast({ title: "Mission réalisée", description: "La mission a été marquée comme réalisée." });
                      } catch (e: any) {
                        toast({ title: "Erreur", description: e.message || "Impossible de marquer la mission réalisée", variant: "destructive" });
                      }
                    }}
                    data-testid={`button-detail-mark-realisee-${detailMission?.id}`}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Marquer réalisée
                  </Button>
                )}
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setDetailOpen(false); openEdit(detailMission); }}
                    data-testid={`button-detail-edit-${detailMission?.id}`}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    Modifier
                  </Button>
                )}
                {canDeleteMission(detailMission) && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => { setDetailOpen(false); setDeleteConfirmId(detailMission.id); }}
                    data-testid={`button-detail-delete-${detailMission?.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </DialogFooter>
            );
          })()}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette mission ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette mission sera définitivement supprimée. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!deleteConfirmId) return;
                try {
                  await deleteMutation.mutateAsync(deleteConfirmId);
                  toast({ title: "Mission supprimée" });
                } catch (err: any) {
                  const msg = err?.message || "Erreur inconnue";
                  toast({ variant: "destructive", title: "Erreur", description: msg.includes("403") ? "Vous n'avez pas les droits pour supprimer cette mission." : msg });
                }
                setDeleteConfirmId(null);
              }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Ajouter une tâche logistique</DialogTitle>
            <DialogDescription>
              {taskMission && (
                <span>Mission : {taskMission.eventName || taskMission.missionType}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="space-y-2">
              <Label>Type de tâche</Label>
              <Select value={taskType} onValueChange={v => { setTaskType(v); setTaskTitle(TASK_TYPE_LABELS[v] || ""); }}>
                <SelectTrigger data-testid="select-logistic-task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TASK_TYPE_LABELS).map(([val, label]) => (
                    <SelectItem key={val} value={val}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Titre</Label>
              <Input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder={TASK_TYPE_LABELS[taskType]} data-testid="input-logistic-task-title" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreateLogisticTask} disabled={createTaskMutation.isPending || !taskTitle} data-testid="button-submit-logistic-task">
                {createTaskMutation.isPending ? "Création..." : "Créer la tâche"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
